import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Props for AiRecognitionIndicator component
 */
interface AiRecognitionIndicatorProps {
  /**
   * Optional 1-based index of the die currently being recognised.
   * When provided alongside `total`, the label reads
   * "Frog is chewing die {current} of {total}…".
   */
  current?: number;
  /** Optional total number of dice in the roll. */
  total?: number;
  /** Optional additional CSS classes for the outer pill. */
  className?: string;
}

/**
 * Small calm "Frog is chewing…" status pill shown while the recognition
 * pipeline is in flight (TICKET-296).
 *
 * Visually matches the camera-overlay pills used for video capture state
 * (rounded-full, backdrop-blur, small text). Designed for inline placement
 * — does NOT take over the screen.
 *
 * Reused intent: future deferred operations (video upload, Discord post)
 * could surface a similar pill, so the label is parameterised but kept
 * to a single phrasing for now to avoid premature abstraction.
 *
 * @example
 * // Single-die roll
 * <AiRecognitionIndicator />
 *
 * @example
 * // Multi-die roll, currently on die 2 of 3
 * <AiRecognitionIndicator current={2} total={3} />
 */
export function AiRecognitionIndicator({
  current,
  total,
  className
}: AiRecognitionIndicatorProps) {
  const showCounter = typeof current === 'number' && typeof total === 'number' && total > 1;
  const label = showCounter
    ? `Frog is chewing die ${current} of ${total}…`
    : 'Frog is chewing…';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cn(
        'flex items-center gap-2 rounded-full border border-white/10 bg-card/80 px-4 py-2 text-sm text-foreground backdrop-blur-sm shadow-lg',
        className
      )}
    >
      <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
